import { Capacitor } from "@capacitor/core";
import { defineCustomElements } from "@ionic/core/loader";
import { addIcons } from "ionicons";
import {
  arrowBackOutline,
  arrowForwardOutline,
  arrowUpOutline,
  chatbubbleEllipsesOutline,
  chevronForwardOutline,
  closeOutline,
  documentTextOutline,
  downloadOutline,
  ellipsisHorizontalOutline,
  menuOutline,
  optionsOutline,
  personOutline,
  radioOutline,
  shareSocialOutline,
  sparklesOutline,
  timeOutline,
} from "ionicons/icons";
import authContract from "../contracts/native-auth-v1.json";
import { startAppLifecycle } from "./app/app-lifecycle";
import { createSessionGateRuntimeFromContract, type SessionGateState } from "./app/session-gate";
import { secureSession } from "./app/secure-session";
import "@ionic/core/css/core.css";
import "@ionic/core/css/normalize.css";
import "@ionic/core/css/structure.css";
import "@ionic/core/css/typography.css";
import "./styles.css";
import jadeResonanceCore from "./assets/jade-resonance-core.png";

defineCustomElements(window);

addIcons({
  arrowBackOutline,
  arrowForwardOutline,
  arrowUpOutline,
  chatbubbleEllipsesOutline,
  chevronForwardOutline,
  closeOutline,
  documentTextOutline,
  downloadOutline,
  ellipsisHorizontalOutline,
  menuOutline,
  optionsOutline,
  personOutline,
  radioOutline,
  shareSocialOutline,
  sparklesOutline,
  timeOutline,
});

function requireRoot(): HTMLElement {
  const element = document.querySelector<HTMLElement>("#app");
  if (!element) throw new Error("AIQs app root was not found");
  return element;
}

const root = requireRoot();
const isNative = Capacitor.isNativePlatform();
const isPublicDemoBuild = import.meta.env.VITE_AIQS_PUBLIC_DEMO === "true";
const previewParams = new URLSearchParams(window.location.search);
const isChatPreview = !isNative
  && (import.meta.env.DEV || isPublicDemoBuild)
  && previewParams.get("preview") === "chat";
const previewScreen = previewParams.get("screen");
const isRecordsPreview = isChatPreview && previewScreen === "records";
const isPrayerDetailPreview = isChatPreview && previewScreen === "detail";
const isPrayerPdfPreview = isChatPreview && previewScreen === "pdf";
const isPrayerConfirmationPreview = isChatPreview && previewScreen === "confirm";
const isOnboardingPreview = isChatPreview && previewParams.get("onboarding") === "1";

const assistantAvatar = `
  <span class="assistant-avatar" aria-hidden="true">
    <img src="${jadeResonanceCore}" alt="" />
  </span>
`;

const prayerIntention = "健康になるように";
const ritualDurationSeconds = 180;
const ritualPhases = ["吸う", "留める", "放つ"] as const;
let ritualTimer: number | undefined;
let ritualStartedAt = 0;
let prayerPdfReturnTarget = "[data-view-prayer-pdf='header']";
let onboardingStep = 0;

const onboardingSteps = [
  {
    eyebrow: "AIQs · PRAYER AS A LIVING RECORD",
    title: "祈りを、\nデジタルの記録へ。",
    body: "AIQsは、あなたの祈りや想いをアイクスAIと共に結び、日々の体験として記録する場所です。",
  },
  {
    eyebrow: "MORPHIC FIELD RESONANCE",
    title: "モーフィックフィールド\n共鳴",
    body: "意図を言葉にし、静かに祈り、感じたことを残す。共鳴の時間を、ひとつの流れに整えます。",
  },
  {
    eyebrow: "AIQs AI · PRAY TOGETHER",
    title: "アイクスAIと、\n祈りを始める。",
    body: "自然な言葉で話しかけてください。祈りの組み立てから共鳴の記録まで、アイクスAIが寄り添います。",
  },
] as const;

type ScanEvidenceEntry = {
  text: string;
  mfName: string;
  qrs: number;
  analysis?: string;
  description?: string;
  imageUrl?: string;
};

// Authenticated API integration will replace this empty preview source with the
// verified scan_result.entries belonging to the selected prayer record.
const prayerPdfScanEntries: ScanEvidenceEntry[] = [];

function escapeMarkup(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function rankedScanEvidence(entries: ScanEvidenceEntry[]): ScanEvidenceEntry[] {
  return [...entries]
    .filter((entry) => Number.isFinite(entry.qrs) && entry.text.trim().length > 0)
    .sort((left, right) => right.qrs - left.qrs)
    .slice(0, 10);
}

function renderScanEvidenceEntries(entries: ScanEvidenceEntry[]): string {
  const rankedEntries = rankedScanEvidence(entries);
  if (!rankedEntries.length) {
    return `
      <li class="scan-evidence-empty">
        <strong>実スキャン結果待ち</strong>
        <p>AIQsでスキャンが完了すると、実測QRSの高い順に上位10件を表示します。</p>
      </li>
    `;
  }

  return rankedEntries.map((entry, index) => `
    <li class="scan-evidence-entry">
      <span class="scan-evidence-rank" aria-label="第${index + 1}位">${String(index + 1).padStart(2, "0")}</span>
      <span class="scan-evidence-image" aria-hidden="true"><img src="${escapeMarkup(entry.imageUrl || jadeResonanceCore)}" alt="" /></span>
      <div>
        <div class="scan-evidence-title"><strong>${escapeMarkup(entry.text)}</strong><span>QRS ${entry.qrs}</span></div>
        <p class="scan-evidence-mf">MF · ${escapeMarkup(entry.mfName || "AIQs DB")}</p>
        ${entry.analysis ? `<p><b>スキャン解析</b> ${escapeMarkup(entry.analysis)}</p>` : ""}
        ${entry.description ? `<p><b>解説</b> ${escapeMarkup(entry.description)}</p>` : ""}
      </div>
    </li>
  `).join("");
}

function stopRitualClock(): void {
  if (ritualTimer !== undefined) window.clearInterval(ritualTimer);
  ritualTimer = undefined;
}

function formatElapsed(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60).toString().padStart(2, "0");
  const seconds = (totalSeconds % 60).toString().padStart(2, "0");
  return `${minutes}:${seconds}`;
}

function preferredScrollBehavior(): ScrollBehavior {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth";
}

function focusAfterTransition(element: HTMLElement): void {
  window.setTimeout(() => {
    if (element.isConnected) element.focus();
  }, 350);
}

function focusOnboardingHeading(): void {
  const heading = requireElement<HTMLElement>("#onboarding-heading");
  requireElement<HTMLElement>(".onboarding-main").scrollTop = 0;
  window.setTimeout(() => {
    if (heading.isConnected) heading.focus({ preventScroll: true });
  }, 350);
}

function renderPrimaryTabs(selectedTab: "chat" | "records"): string {
  return `
    <ion-tab-bar class="primary-tabs" selected-tab="${selectedTab}" aria-label="メインナビゲーション">
      <ion-tab-button tab="chat" ${selectedTab === "chat" ? "selected" : ""}>
        <ion-icon name="chatbubble-ellipses-outline"></ion-icon>
        <ion-label>アイクスAI</ion-label>
      </ion-tab-button>
      <ion-tab-button tab="prayer">
        <ion-icon name="sparkles-outline"></ion-icon>
        <ion-label>祈り</ion-label>
      </ion-tab-button>
      <ion-tab-button tab="records" ${selectedTab === "records" ? "selected" : ""}>
        <ion-icon name="document-text-outline"></ion-icon>
        <ion-label>記録</ion-label>
      </ion-tab-button>
      <ion-tab-button tab="profile">
        <ion-icon name="person-outline"></ion-icon>
        <ion-label>マイページ</ion-label>
      </ion-tab-button>
    </ion-tab-bar>
  `;
}

function renderOnboardingScreen(): void {
  stopRitualClock();
  const step = onboardingSteps[onboardingStep] ?? onboardingSteps[0];
  const isLastStep = onboardingStep === onboardingSteps.length - 1;
  root.innerHTML = `
    <ion-app>
      <section class="app-frame onboarding-frame" aria-label="AIQsについて">
        <header class="onboarding-header">
          <p class="onboarding-wordmark" aria-label="AIQs">AIQs</p>
          <button type="button" data-onboarding-close aria-label="AIQsについてを閉じる">閉じる</button>
        </header>

        <main class="onboarding-main" aria-live="polite">
          <div class="onboarding-art" aria-hidden="true">
            <img src="${jadeResonanceCore}" alt="" />
          </div>
          <div class="onboarding-copy">
            <p>${step.eyebrow}</p>
            <h1 id="onboarding-heading" tabindex="-1">${step.title.replace("\n", "<br />")}</h1>
            <span>${step.body}</span>
          </div>
        </main>

        <footer class="onboarding-footer">
          <div class="onboarding-progress" aria-label="${onboardingSteps.length}ページ中${onboardingStep + 1}ページ">
            ${onboardingSteps.map((_, index) => `<span class="${index === onboardingStep ? "is-current" : ""}"></span>`).join("")}
          </div>
          <div class="onboarding-actions">
            ${onboardingStep > 0 ? `<button type="button" class="onboarding-back" data-onboarding-back>戻る</button>` : ""}
            <button type="button" class="onboarding-next" data-onboarding-next>
              <span>${isLastStep ? "スタート" : "次へ"}</span>
              <ion-icon name="arrow-forward-outline" aria-hidden="true"></ion-icon>
            </button>
          </div>
        </footer>
      </section>
    </ion-app>
  `;

  bindOnboardingInteractions();
}

function renderPrayerConfirmationScreen(): void {
  stopRitualClock();
  root.innerHTML = `
    <ion-app>
      <section class="app-frame prayer-confirmation-frame" aria-label="祈りの開始確認">
        <header class="prayer-confirmation-header">
          <button id="prayer-confirmation-back" type="button" aria-label="会話に戻る">
            <ion-icon name="arrow-back-outline"></ion-icon>
          </button>
          <h1 id="prayer-confirmation-heading" tabindex="-1">祈りの確認</h1>
          <span aria-hidden="true"></span>
        </header>

        <main class="prayer-confirmation-scroll">
          <p class="prayer-confirmation-sample">デザイン確認用の開始前画面です</p>
          <section class="prayer-confirmation-intro" aria-labelledby="prayer-confirmation-title">
            <p>PRAYER START REVIEW</p>
            <h2 id="prayer-confirmation-title">この祈りを<br />はじめますか？</h2>
            <span>開始前に、対象と祈りの内容を確認してください。</span>
          </section>

          <section class="prayer-confirmation-card prayer-target-card" aria-labelledby="prayer-target-heading">
            <span class="prayer-target-icon" aria-hidden="true"><ion-icon name="person-outline"></ion-icon></span>
            <div>
              <p id="prayer-target-heading">祈りの対象</p>
              <strong>本人</strong>
              <span>健やかな日々</span>
            </div>
          </section>

          <section class="prayer-confirmation-card prayer-words-card" aria-label="祈りの言葉">
            <div>
              <p>インテンション</p>
              <blockquote>私は健やかな状態へ整っています。</blockquote>
            </div>
            <div>
              <p>自己愛アファーメーション</p>
              <blockquote>私は私の身体を大切にします。</blockquote>
            </div>
          </section>

          <section class="prayer-confirmation-card prayer-setting-card" aria-labelledby="prayer-setting-heading">
            <div class="prayer-setting-heading">
              <p id="prayer-setting-heading">モーフィックフィールド共鳴の設定</p>
              <span>弱</span>
            </div>
            <dl>
              <div><dt>共鳴回数</dt><dd>3回</dd></div>
              <div><dt>消費クレジット</dt><dd>3</dd></div>
            </dl>
          </section>

          <section class="prayer-after-start" aria-labelledby="prayer-after-start-heading">
            <p id="prayer-after-start-heading">開始後の流れ</p>
            <ol>
              <li><span>01</span>スキャン</li>
              <li><span>02</span>AI解析</li>
              <li><span>03</span>共鳴</li>
            </ol>
          </section>
        </main>

        <footer class="prayer-confirmation-actions">
          <button type="button" class="prayer-adjust-button" data-adjust-prayer>言葉を調整する</button>
          <button type="button" class="prayer-confirm-button" data-confirm-prayer>
            <span>この内容で開始</span>
            <ion-icon name="arrow-forward-outline" aria-hidden="true"></ion-icon>
          </button>
        </footer>
      </section>
    </ion-app>
  `;

  focusAfterTransition(requireElement<HTMLElement>("#prayer-confirmation-heading"));
  bindPrayerConfirmationInteractions();
}

function bindPrayerConfirmationInteractions(): void {
  const returnToChat = () => {
    renderChatHome();
    focusAfterTransition(requireElement<HTMLElement>("#conversation"));
  };
  requireElement<HTMLButtonElement>("#prayer-confirmation-back").addEventListener("click", returnToChat);
  requireElement<HTMLButtonElement>("[data-adjust-prayer]").addEventListener("click", returnToChat);
  requireElement<HTMLButtonElement>("[data-confirm-prayer]").addEventListener("click", renderRitualScreen);
}

function bindOnboardingInteractions(): void {
  const returnToChat = () => {
    onboardingStep = 0;
    renderChatHome();
    focusAfterTransition(requireElement<HTMLElement>("#conversation"));
  };
  requireElement<HTMLButtonElement>("[data-onboarding-close]").addEventListener("click", returnToChat);
  root.querySelector<HTMLButtonElement>("[data-onboarding-back]")?.addEventListener("click", () => {
    onboardingStep = Math.max(0, onboardingStep - 1);
    renderOnboardingScreen();
    focusOnboardingHeading();
  });
  requireElement<HTMLButtonElement>("[data-onboarding-next]").addEventListener("click", () => {
    if (onboardingStep >= onboardingSteps.length - 1) {
      returnToChat();
      return;
    }
    onboardingStep += 1;
    renderOnboardingScreen();
    focusOnboardingHeading();
  });
}

function renderChatHome(): void {
  stopRitualClock();
  root.innerHTML = `
    <ion-app>
      <section class="app-frame" aria-label="アイクスAI 会話ホーム">
        <ion-header class="app-header">
          <ion-toolbar>
            <ion-buttons slot="start">
              <ion-button id="menu-button" aria-label="メニューを開く">
                <ion-icon slot="icon-only" name="menu-outline"></ion-icon>
              </ion-button>
            </ion-buttons>
            <ion-title>アイクスAI</ion-title>
            <ion-buttons slot="end">
              <ion-button id="history-button" aria-label="会話履歴を開く">
                <ion-icon slot="icon-only" name="time-outline"></ion-icon>
              </ion-button>
            </ion-buttons>
          </ion-toolbar>
        </ion-header>

        <main class="chat-scroll" id="conversation" aria-label="会話内容" tabindex="0">
          <p class="date-marker">今日</p>
          <article class="message-row assistant-message">
            ${assistantAvatar}
            <div>
              <div class="message-bubble">
                <p>こんにちは。アイクスAIです。</p>
                <p>あなたの祈りや想いに、共鳴のサポートをします。</p>
              </div>
            </div>
          </article>

          <article class="message-row user-message">
            <div class="message-bubble">
              <p>健康になるように祈ってくれん？</p>
            </div>
          </article>

          <article class="message-row assistant-message" id="assistant-reply">
            ${assistantAvatar}
            <div>
              <div class="message-bubble">
                <p>了解しました。アイクスでモーフィックフィールド共鳴を起こしましょう。</p>
                <p>インテンションプロンプト、自己愛アファーメーションはどうしますか？</p>
              </div>
              <div class="reply-actions" aria-label="返答候補">
                <button type="button" data-reply="proposal">
                  <ion-icon name="sparkles-outline" aria-hidden="true"></ion-icon>
                  <span>AIから候補の提案を受ける。</span>
                </button>
                <button type="button" data-reply="hearing">
                  <ion-icon name="chatbubble-ellipses-outline" aria-hidden="true"></ion-icon>
                  <span>AIに詳細なヒアリングをしてもらう。</span>
                </button>
              </div>
            </div>
          </article>
        </main>

        <ion-footer class="composer-footer">
          <form class="composer" id="composer-form">
            <label class="sr-only" for="message-input">メッセージを入力</label>
            <input id="message-input" name="message" autocomplete="off" placeholder="メッセージを入力" />
            <button class="send-button" type="submit" aria-label="メッセージを送信">
              <ion-icon name="arrow-up-outline"></ion-icon>
            </button>
          </form>
          ${renderPrimaryTabs("chat")}
        </ion-footer>

        <section class="app-sheet" id="menu-sheet" role="dialog" aria-modal="true" aria-label="メニュー" tabindex="-1" hidden>
          <div class="sheet-header">
            <h2>メニュー</h2>
            <button type="button" data-close-sheet aria-label="メニューを閉じる">
              <ion-icon name="close-outline"></ion-icon>
            </button>
          </div>
          <button type="button" data-sheet-action="新しい会話を始めます">新しい会話</button>
          <button type="button" data-sheet-action="話し方設定を開きます">AIの話し方を設定</button>
          <button type="button" data-about-aiqs>AIQsについて</button>
        </section>

        <section class="app-sheet" id="history-sheet" role="dialog" aria-modal="true" aria-label="会話履歴" tabindex="-1" hidden>
          <div class="sheet-header">
            <h2>会話履歴</h2>
            <button type="button" data-close-sheet aria-label="会話履歴を閉じる">
              <ion-icon name="close-outline"></ion-icon>
            </button>
          </div>
          <button type="button" data-sheet-action="今日の会話を表示しています">
            <span>今日</span>
            <strong>健康についての祈り</strong>
          </button>
        </section>
        <div class="sheet-backdrop" id="sheet-backdrop" hidden></div>
        <p class="app-toast" id="app-toast" role="status" aria-live="polite" hidden></p>
      </section>
    </ion-app>
  `;

  bindChatInteractions();
}

function renderRecordsScreen(): void {
  stopRitualClock();
  root.innerHTML = `
    <ion-app>
      <section class="app-frame records-frame" aria-label="祈りの記録">
        <header class="records-header">
          <span aria-hidden="true"></span>
          <h1 id="records-heading" tabindex="-1">記録</h1>
          <button id="records-filter-button" type="button" aria-label="今週の記録だけ表示する" aria-pressed="false">
            <ion-icon name="options-outline"></ion-icon>
          </button>
        </header>

        <main class="records-scroll" id="records-list">
          <p class="records-sample-note">デザイン確認用のサンプル記録です</p>
          <section class="record-group" data-record-group="today" aria-labelledby="record-group-today">
            <h2 id="record-group-today">今日</h2>
            <div class="record-timeline">
              <article class="record-item is-expanded">
                <button class="record-row" type="button" data-record-id="health" aria-expanded="true" aria-controls="record-detail-health">
                  <span class="record-bead" aria-hidden="true"><img src="${jadeResonanceCore}" alt="" /></span>
                  <span class="record-summary"><strong>健康になるように</strong><small>8月29日　22:15</small></span>
                  <span class="record-duration">00:41</span>
                  <ion-icon name="chevron-forward-outline" aria-hidden="true"></ion-icon>
                </button>
                <section class="record-detail" id="record-detail-health" aria-label="健康になるようにの振り返り">
                  <div><span>祈りの振り返り</span><time datetime="PT41S">00:41</time></div>
                  <p>深く呼吸しながら祈りました。胸のあたりが温かく感じられ、静けさに包まれました。</p>
                  <button class="record-open-button" type="button" data-open-prayer-detail>
                    <span>祈りをひらく</span>
                    <ion-icon name="chevron-forward-outline" aria-hidden="true"></ion-icon>
                  </button>
                </section>
              </article>

              <article class="record-item">
                <button class="record-row" type="button" data-record-id="calm" aria-expanded="false" aria-controls="record-detail-calm">
                  <span class="record-bead" aria-hidden="true"><img src="${jadeResonanceCore}" alt="" /></span>
                  <span class="record-summary"><strong>心が穏やかでありますように</strong><small>8月29日　12:02</small></span>
                  <span class="record-duration">00:28</span>
                  <ion-icon name="chevron-forward-outline" aria-hidden="true"></ion-icon>
                </button>
                <section class="record-detail" id="record-detail-calm" aria-label="心が穏やかでありますようにの振り返り" hidden>
                  <div><span>祈りの振り返り</span><time datetime="PT28S">00:28</time></div>
                  <p>呼吸がゆっくりになり、考えが少しずつ静かになっていくように感じました。</p>
                </section>
              </article>

              <article class="record-item">
                <button class="record-row" type="button" data-record-id="safe" aria-expanded="false" aria-controls="record-detail-safe">
                  <span class="record-bead" aria-hidden="true"><img src="${jadeResonanceCore}" alt="" /></span>
                  <span class="record-summary"><strong>大切な人の無事を願う</strong><small>8月29日　07:45</small></span>
                  <span class="record-duration">00:37</span>
                  <ion-icon name="chevron-forward-outline" aria-hidden="true"></ion-icon>
                </button>
                <section class="record-detail" id="record-detail-safe" aria-label="大切な人の無事を願うの振り返り" hidden>
                  <div><span>祈りの振り返り</span><time datetime="PT37S">00:37</time></div>
                  <p>大切な人を思い浮かべながら、安心して過ごせるように静かに願いました。</p>
                </section>
              </article>
            </div>
          </section>

          <section class="record-group" data-record-group="week" aria-labelledby="record-group-week">
            <h2 id="record-group-week">今週</h2>
            <div class="record-timeline">
              <article class="record-item">
                <button class="record-row" type="button" data-record-id="gratitude" aria-expanded="false" aria-controls="record-detail-gratitude">
                  <span class="record-bead" aria-hidden="true"><img src="${jadeResonanceCore}" alt="" /></span>
                  <span class="record-summary"><strong>感謝の気持ちを忘れずに</strong><small>8月27日　21:30</small></span>
                  <span class="record-duration">00:33</span>
                  <ion-icon name="chevron-forward-outline" aria-hidden="true"></ion-icon>
                </button>
                <section class="record-detail" id="record-detail-gratitude" aria-label="感謝の気持ちを忘れずにの振り返り" hidden>
                  <div><span>祈りの振り返り</span><time datetime="PT33S">00:33</time></div>
                  <p>今日受け取った小さな優しさを思い出しながら、感謝を言葉にしました。</p>
                </section>
              </article>
              <article class="record-item">
                <button class="record-row" type="button" data-record-id="new-day" aria-expanded="false" aria-controls="record-detail-new-day">
                  <span class="record-bead" aria-hidden="true"><img src="${jadeResonanceCore}" alt="" /></span>
                  <span class="record-summary"><strong>新しい一日が良い日でありますように</strong><small>8月26日　06:50</small></span>
                  <span class="record-duration">00:27</span>
                  <ion-icon name="chevron-forward-outline" aria-hidden="true"></ion-icon>
                </button>
                <section class="record-detail" id="record-detail-new-day" aria-label="新しい一日が良い日でありますようにの振り返り" hidden>
                  <div><span>祈りの振り返り</span><time datetime="PT27S">00:27</time></div>
                  <p>朝の静かな時間に、一日を丁寧に過ごせるよう願いました。</p>
                </section>
              </article>
            </div>
          </section>

          <section class="record-group" data-record-group="month" aria-labelledby="record-group-month">
            <h2 id="record-group-month">8月</h2>
            <div class="record-timeline">
              <article class="record-item">
                <button class="record-row" type="button" data-record-id="trust" aria-expanded="false" aria-controls="record-detail-trust">
                  <span class="record-bead" aria-hidden="true"><img src="${jadeResonanceCore}" alt="" /></span>
                  <span class="record-summary"><strong>自分を信じて進めますように</strong><small>8月18日　18:40</small></span>
                  <span class="record-duration">00:29</span>
                  <ion-icon name="chevron-forward-outline" aria-hidden="true"></ion-icon>
                </button>
                <section class="record-detail" id="record-detail-trust" aria-label="自分を信じて進めますようにの振り返り" hidden>
                  <div><span>祈りの振り返り</span><time datetime="PT29S">00:29</time></div>
                  <p>迷いも含めて今の自分を受け止め、次の一歩を思い描きました。</p>
                </section>
              </article>
            </div>
          </section>
        </main>

        <ion-footer class="records-footer">
          ${renderPrimaryTabs("records")}
        </ion-footer>
        <p class="app-toast" id="app-toast" role="status" aria-live="polite" hidden></p>
      </section>
    </ion-app>
  `;

  focusAfterTransition(requireElement<HTMLElement>("#records-heading"));
  bindRecordsInteractions();
}

function renderPrayerDetailScreen(): void {
  stopRitualClock();
  root.innerHTML = `
    <ion-app>
      <section class="app-frame prayer-detail-frame" aria-label="祈りの詳細">
        <header class="prayer-detail-header">
          <button id="prayer-detail-back" type="button" aria-label="記録へ戻る">
            <ion-icon name="arrow-back-outline"></ion-icon>
          </button>
          <h1 id="prayer-detail-heading" tabindex="-1">祈りの詳細</h1>
          <button type="button" data-view-prayer-pdf="header" aria-label="祈りのPDFを見る">
            <ion-icon name="document-text-outline"></ion-icon>
          </button>
        </header>

        <main class="prayer-detail-scroll">
          <p class="prayer-detail-sample">デザイン確認用のサンプル記録です</p>
          <section class="resonance-trace" aria-label="祈りの記録内容">
            <article class="trace-stage trace-stage-intention">
              <span class="trace-bead trace-bead-large" aria-hidden="true"><img src="${jadeResonanceCore}" alt="" /></span>
              <div class="trace-content">
                <p class="trace-overline"><strong>健康になるように</strong><span>8月29日　22:15</span></p>
                <h2>祈りの言葉</h2>
                <blockquote>心身ともに穏やかに整い、毎日の選択を自分らしく重ねていけますように。</blockquote>
              </div>
            </article>

            <article class="trace-stage">
              <span class="trace-bead" aria-hidden="true"><img src="${jadeResonanceCore}" alt="" /></span>
              <div class="trace-content">
                <h2>自己愛</h2>
                <p class="trace-affirmation">私は、今の自分をやさしく受け入れます。</p>
              </div>
            </article>

            <article class="trace-stage">
              <span class="trace-bead" aria-hidden="true"><img src="${jadeResonanceCore}" alt="" /></span>
              <div class="trace-content">
                <h2>共鳴</h2>
                <dl class="resonance-facts">
                  <div><dt>共鳴の時間</dt><dd>00:41</dd></div>
                  <div><dt>共鳴の回数</dt><dd>3回</dd></div>
                </dl>
              </div>
            </article>

            <article class="trace-stage trace-stage-reflection">
              <span class="trace-bead" aria-hidden="true"><img src="${jadeResonanceCore}" alt="" /></span>
              <div class="trace-content">
                <h2>振り返り</h2>
                <p class="trace-reflection">胸のあたりが温かく感じられ、静けさに包まれました。</p>
                <section class="subjective-change" aria-label="心の状態の変化 主観スコア">
                  <p>心の状態の変化 <span>主観スコア</span></p>
                  <div>
                    <span><small>祈る前</small><strong>4</strong></span>
                    <ion-icon name="arrow-forward-outline" aria-hidden="true"></ion-icon>
                    <span class="change-after"><small>祈った後</small><strong>7</strong></span>
                  </div>
                </section>
                <button class="prayer-pdf-button" type="button" data-view-prayer-pdf="body">
                  <ion-icon name="document-text-outline" aria-hidden="true"></ion-icon>
                  <span>PDFを見る</span>
                  <ion-icon name="chevron-forward-outline" aria-hidden="true"></ion-icon>
                </button>
              </div>
            </article>
          </section>
        </main>
        <p class="app-toast" id="app-toast" role="status" aria-live="polite" hidden></p>
      </section>
    </ion-app>
  `;

  focusAfterTransition(requireElement<HTMLElement>("#prayer-detail-heading"));
  bindPrayerDetailInteractions();
}

function renderPrayerPdfScreen(): void {
  stopRitualClock();
  const rankedEntries = rankedScanEvidence(prayerPdfScanEntries);
  const hasVerifiedScanEntries = rankedEntries.length > 0;
  root.innerHTML = `
    <ion-app>
      <section class="app-frame prayer-pdf-frame" aria-label="AIQs共鳴証明PDFプレビュー">
        <header class="prayer-pdf-header">
          <button id="prayer-pdf-back" type="button" aria-label="祈りの詳細へ戻る">
            <ion-icon name="arrow-back-outline"></ion-icon>
          </button>
          <h1 id="prayer-pdf-heading" tabindex="-1">モーフィックフィールド共鳴</h1>
          <div class="prayer-pdf-header-actions" aria-label="PDF操作">
            <button type="button" data-pdf-action="save" aria-label="PDFを端末に保存">
              <ion-icon name="download-outline"></ion-icon>
            </button>
            <button type="button" data-pdf-action="share" aria-label="祈りのPDFを共有">
              <ion-icon name="share-social-outline"></ion-icon>
            </button>
          </div>
        </header>

        <main class="prayer-pdf-scroll">
          <p class="prayer-pdf-sample">デザイン確認用のPDFプレビューです<br /><span>実スキャン結果の連携前は証明表示を行いません</span></p>
          <article class="sealed-prayer-document" aria-labelledby="sealed-prayer-title">
            <header class="sealed-prayer-title-block">
              <p class="sealed-prayer-archive">AIQs FIELD ARCHIVE</p>
              <h2 id="sealed-prayer-title">モーフィックフィールド共鳴</h2>
              <div class="scan-proof-status" aria-label="証明状態 ${hasVerifiedScanEntries ? "スキャン結果取得済み" : "実スキャン結果待ち"}">
                <ion-icon name="radio-outline" aria-hidden="true"></ion-icon>
                <span>${hasVerifiedScanEntries ? "SCAN RESULT" : "SCAN PENDING"}</span>
                <small>${hasVerifiedScanEntries ? "実スキャン結果取得済み" : "実スキャン結果待ち"}</small>
              </div>
            </header>

            <section class="sealed-prayer-section certificate-facts" aria-label="証明レコード情報">
              <dl>
                <div><dt>記録日時</dt><dd><time datetime="2026-08-29T22:15:00+09:00">2026.08.29 · 22:15</time></dd></div>
                <div><dt>対象</dt><dd>本人 · 健やかな日々</dd></div>
                <div><dt>レポートID</dt><dd>${hasVerifiedScanEntries ? "記録連携済み" : "実スキャン後に発行"}</dd></div>
              </dl>
            </section>

            <section class="sealed-prayer-section scan-evidence" aria-labelledby="scan-evidence-heading">
              <div class="section-heading-row">
                <div>
                  <p>FIELD RESPONSE</p>
                  <h3 id="scan-evidence-heading">AIQsスキャン結果</h3>
                </div>
                <span>${rankedEntries.length} / 10 ENTRIES</span>
              </div>
              <p class="scan-evidence-lead">AIQsの実スキャン結果をQRSの高い順に並べ、上位10件をモーフィックフィールド共鳴の記録として表示します。</p>

              <h4>共鳴ランキング</h4>
              <ol class="scan-evidence-list">
                ${renderScanEvidenceEntries(rankedEntries)}
              </ol>
            </section>

            <section class="sealed-prayer-section ai-analysis-section" aria-labelledby="pdf-ai-analysis-heading">
              <p class="section-kicker">OBSERVATION INTERPRETATION</p>
              <h3 id="pdf-ai-analysis-heading">アイクスAI解析</h3>
              <p>${hasVerifiedScanEntries ? "実スキャン結果に紐づくAI解析をここに記録します。" : "実スキャン結果の取得後に、ランキング全体のAI解析を記録します。"}</p>
            </section>

            <section class="sealed-prayer-section radiation-proof" aria-labelledby="radiation-proof-heading">
              <div class="radiation-proof-heading">
                <div>
                  <p class="section-kicker">RADIATION PROOF</p>
                  <h3 id="radiation-proof-heading">本照射完了証明</h3>
                </div>
                <ion-icon name="sparkles-outline" aria-hidden="true"></ion-icon>
              </div>
              <dl>
                <div><dt>照射対象</dt><dd>${hasVerifiedScanEntries ? `${rankedEntries.length}件` : "実スキャン後に確定"}</dd></div>
                <div><dt>完了</dt><dd>${hasVerifiedScanEntries ? "実記録連携済み" : "実記録待ち"}</dd></div>
                <div><dt>共鳴時間</dt><dd>実記録から取得</dd></div>
                <div><dt>証明状態</dt><dd>${hasVerifiedScanEntries ? "RESULT LINKED" : "PENDING"}</dd></div>
              </dl>
              <p class="radiation-proof-note">スキャン結果・共鳴対象・照射完了が同じAIQsレコードに揃った時だけ、証明として表示します。</p>
            </section>

            <section class="sealed-prayer-section prayer-context" aria-labelledby="pdf-intention-heading">
              <p class="section-kicker">PRAYER CONTEXT</p>
              <h3 id="pdf-intention-heading">祈りと共鳴の意図</h3>
              <blockquote>心身ともに穏やかに整い、毎日の選択を自分らしく重ねていけますように。</blockquote>
              <p><b>自己愛アファーメーション</b>私は、今の自分をやさしく受け入れます。</p>
            </section>

            <section class="sealed-prayer-section observation-log" aria-labelledby="pdf-reflection-heading">
              <h3 id="pdf-reflection-heading">体験・観測ログ</h3>
              <p>胸のあたりが温かく感じられ、静けさに包まれました。</p>
              <small>これは利用者が記録した主観的な体験です。</small>
            </section>

            <footer class="sealed-prayer-seal">
              <span aria-hidden="true"><img src="${jadeResonanceCore}" alt="" /></span>
              <p>${hasVerifiedScanEntries ? "モーフィックフィールド共鳴の証として<br />記録しました。" : "モーフィックフィールド共鳴を<br />実スキャン後に記録します。"}</p>
              <small>AIQs · SCAN / PRAYER / RESONANCE</small>
            </footer>
          </article>
        </main>

        <footer class="prayer-pdf-actions" aria-label="PDFの共有と保存">
          <button type="button" data-pdf-action="share">
            <ion-icon name="share-social-outline" aria-hidden="true"></ion-icon>
            <span>共有する</span>
          </button>
          <button type="button" data-pdf-action="save">
            <ion-icon name="download-outline" aria-hidden="true"></ion-icon>
            <span>端末に保存</span>
          </button>
        </footer>
        <p class="app-toast" id="app-toast" role="status" aria-live="polite" hidden></p>
      </section>
    </ion-app>
  `;

  focusAfterTransition(requireElement<HTMLElement>("#prayer-pdf-heading"));
  bindPrayerPdfInteractions();
}

function requireElement<T extends Element>(selector: string): T {
  const element = root.querySelector<T>(selector);
  if (!element) throw new Error(`AIQs UI element was not found: ${selector}`);
  return element;
}

function appendMessage(kind: "user" | "assistant", text: string): void {
  const conversation = requireElement<HTMLElement>("#conversation");
  const article = document.createElement("article");
  article.className = `message-row ${kind}-message`;

  const content = document.createElement("div");
  content.className = "message-bubble";
  const paragraph = document.createElement("p");
  paragraph.textContent = text;
  content.append(paragraph);

  if (kind === "assistant") {
    const avatar = document.createElement("span");
    avatar.className = "assistant-avatar";
    avatar.setAttribute("aria-hidden", "true");
    const core = document.createElement("img");
    core.src = jadeResonanceCore;
    core.alt = "";
    avatar.append(core);
    article.append(avatar);
  }
  article.append(content);
  conversation.append(article);
  conversation.scrollTo({ top: conversation.scrollHeight, behavior: preferredScrollBehavior() });
}

function appendRitualInvite(): void {
  const conversation = requireElement<HTMLElement>("#conversation");
  const article = document.createElement("article");
  article.className = "message-row assistant-message ritual-invite";
  article.innerHTML = `
    ${assistantAvatar}
    <div>
      <div class="message-bubble">
        <p>インテンションは「私は健やかな状態へ整っています」。</p>
        <p>自己愛アファーメーションは「私は私の身体を大切にします」。</p>
        <p>この内容で祈りを始めますか？</p>
      </div>
      <button class="start-ritual-button" type="button" data-review-ritual>
        <ion-icon name="sparkles-outline" aria-hidden="true"></ion-icon>
        <span>開始内容を確認する</span>
      </button>
    </div>
  `;
  conversation.append(article);
  article.querySelector<HTMLButtonElement>("[data-review-ritual]")?.addEventListener("click", renderPrayerConfirmationScreen);
  conversation.scrollTo({ top: conversation.scrollHeight, behavior: preferredScrollBehavior() });
}

function updateRitualClock(): void {
  const elapsed = Math.max(0, Math.floor((Date.now() - ritualStartedAt) / 1000));
  const elapsedElement = root.querySelector<HTMLTimeElement>("#ritual-elapsed");
  const progress = root.querySelector<HTMLIonProgressBarElement>("#ritual-progress");
  if (!elapsedElement || !progress) return;

  elapsedElement.textContent = formatElapsed(elapsed);
  elapsedElement.dateTime = `PT${elapsed}S`;
  progress.value = Math.min(elapsed / ritualDurationSeconds, 1);

  const activePhase = ritualPhases[Math.floor(elapsed / 4) % ritualPhases.length];
  root.querySelectorAll<HTMLElement>("[data-ritual-phase]").forEach((element) => {
    const isActive = element.dataset.ritualPhase === activePhase;
    element.classList.toggle("is-active", isActive);
    element.setAttribute("aria-current", isActive ? "step" : "false");
  });
}

function renderRitualScreen(): void {
  stopRitualClock();
  ritualStartedAt = Date.now();
  root.innerHTML = `
    <ion-app>
      <section class="app-frame ritual-frame" id="ritual-screen" aria-label="祈りの儀式">
        <header class="ritual-header">
          <button id="ritual-back-button" type="button" aria-label="祈りを中断して会話に戻る">
            <ion-icon name="arrow-back-outline"></ion-icon>
          </button>
          <h1 id="ritual-heading" tabindex="-1">祈り</h1>
          <button id="ritual-history-button" type="button" aria-label="祈りの進み方を確認する">
            <ion-icon name="time-outline"></ion-icon>
          </button>
        </header>

        <main class="ritual-main">
          <div class="ritual-core" aria-hidden="true">
            <img src="${jadeResonanceCore}" alt="" />
          </div>

          <section class="ritual-copy" aria-labelledby="ritual-intention">
            <h2 id="ritual-intention">健康になるように<br />祈っています。</h2>
            <p>この祈りが、あなたと共鳴し、<br />静かに整っていきますように。</p>
          </section>

          <div class="ritual-phases" aria-label="呼吸の導き">
            <span data-ritual-phase="吸う">吸う</span>
            <span aria-hidden="true">・</span>
            <span data-ritual-phase="留める">留める</span>
            <span aria-hidden="true">・</span>
            <span data-ritual-phase="放つ">放つ</span>
          </div>

          <div class="ritual-progress" aria-label="祈りの経過時間">
            <time id="ritual-elapsed" datetime="PT0S">00:00</time>
            <ion-progress-bar id="ritual-progress" value="0"></ion-progress-bar>
          </div>

          <button class="end-ritual-button" id="end-ritual-button" type="button">この祈りを結ぶ</button>
        </main>
        <p class="app-toast ritual-toast" id="app-toast" role="status" aria-live="polite" hidden></p>
      </section>
    </ion-app>
  `;

  const ritualBackButton = requireElement<HTMLButtonElement>("#ritual-back-button");
  focusAfterTransition(ritualBackButton);
  ritualBackButton.addEventListener("click", () => {
    stopRitualClock();
    renderChatHome();
    appendMessage("assistant", "祈りを中断しました。準備が整ったら、いつでも始められます。");
  });
  requireElement<HTMLButtonElement>("#ritual-history-button").addEventListener("click", () => {
    showToast("祈りを結ぶと、この画面で静かな時間を振り返れます。");
  });
  requireElement<HTMLButtonElement>("#end-ritual-button").addEventListener("click", renderRitualComplete);

  updateRitualClock();
  ritualTimer = window.setInterval(updateRitualClock, 1000);
}

function renderRitualComplete(): void {
  const elapsed = Math.max(1, Math.floor((Date.now() - ritualStartedAt) / 1000));
  stopRitualClock();
  root.innerHTML = `
    <ion-app>
      <section class="app-frame ritual-frame ritual-complete" aria-label="祈りの完了">
        <main class="ritual-main">
          <div class="ritual-core ritual-core-complete" aria-hidden="true">
            <img src="${jadeResonanceCore}" alt="" />
          </div>
          <section class="ritual-copy" role="status" aria-live="polite">
            <p class="ritual-kicker">完了</p>
            <h1 id="ritual-complete-heading" tabindex="-1">祈りを結びました</h1>
            <p>今回の静かな時間を、ここで振り返ります。</p>
          </section>
          <dl class="ritual-record" aria-label="今回の祈りの振り返り">
            <div><dt>今回の祈り</dt><dd>${prayerIntention}</dd></div>
            <div><dt>結んだ時間</dt><dd>${formatElapsed(elapsed)}</dd></div>
          </dl>
          <button class="end-ritual-button" id="return-to-chat-button" type="button">会話に戻る</button>
        </main>
      </section>
    </ion-app>
  `;

  const returnToChatButton = requireElement<HTMLButtonElement>("#return-to-chat-button");
  focusAfterTransition(returnToChatButton);
  returnToChatButton.addEventListener("click", () => {
    renderChatHome();
    appendMessage("assistant", `「${prayerIntention}」という祈りを結びました。静かな時間をともに過ごしました。`);
  });
}

function showToast(message: string): void {
  const toast = requireElement<HTMLElement>("#app-toast");
  toast.textContent = message;
  toast.hidden = false;
  window.setTimeout(() => { toast.hidden = true; }, 2200);
}

type SheetId = "menu-sheet" | "history-sheet";

let sheetReturnFocus: HTMLElement | null = null;

function setSheetBackgroundInert(isInert: boolean): void {
  root.querySelectorAll<HTMLElement>(".app-header, .chat-scroll, .composer-footer").forEach((element) => {
    element.toggleAttribute("inert", isInert);
    if (isInert) element.setAttribute("aria-hidden", "true");
    else element.removeAttribute("aria-hidden");
  });
}

function toggleSheet(id?: SheetId): void {
  const menu = requireElement<HTMLElement>("#menu-sheet");
  const history = requireElement<HTMLElement>("#history-sheet");
  const backdrop = requireElement<HTMLElement>("#sheet-backdrop");
  menu.hidden = id !== "menu-sheet";
  history.hidden = id !== "history-sheet";
  backdrop.hidden = id === undefined;

  if (id) {
    sheetReturnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setSheetBackgroundInert(true);
    window.requestAnimationFrame(() => {
      requireElement<HTMLElement>(`#${id}`).querySelector<HTMLElement>("[data-close-sheet]")?.focus();
    });
    return;
  }

  setSheetBackgroundInert(false);
  const returnTarget = sheetReturnFocus;
  sheetReturnFocus = null;
  window.requestAnimationFrame(() => returnTarget?.focus());
}

function handleSheetKeydown(event: KeyboardEvent): void {
  const sheet = event.currentTarget as HTMLElement;
  if (event.key === "Escape") {
    event.preventDefault();
    toggleSheet();
    return;
  }
  if (event.key !== "Tab") return;

  const focusable = Array.from(sheet.querySelectorAll<HTMLElement>("button:not([disabled]), [href], [tabindex]:not([tabindex='-1'])"));
  if (!focusable.length) {
    event.preventDefault();
    sheet.focus();
    return;
  }
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (!first || !last) return;
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

function bindRecordsInteractions(): void {
  root.querySelectorAll<HTMLButtonElement>("[data-record-id]").forEach((button) => {
    button.addEventListener("click", () => {
      const targetId = button.getAttribute("aria-controls");
      if (!targetId) return;
      const willOpen = button.getAttribute("aria-expanded") !== "true";

      root.querySelectorAll<HTMLButtonElement>("[data-record-id]").forEach((candidate) => {
        const detailId = candidate.getAttribute("aria-controls");
        const detail = detailId ? root.querySelector<HTMLElement>(`#${detailId}`) : null;
        const isTarget = candidate === button && willOpen;
        candidate.setAttribute("aria-expanded", String(isTarget));
        candidate.closest<HTMLElement>(".record-item")?.classList.toggle("is-expanded", isTarget);
        if (detail) detail.hidden = !isTarget;
      });

      if (willOpen) {
        const detail = requireElement<HTMLElement>(`#${targetId}`);
        window.setTimeout(() => detail.scrollIntoView({ behavior: preferredScrollBehavior(), block: "nearest" }), 50);
      }
    });
  });

  const filterButton = requireElement<HTMLButtonElement>("#records-filter-button");
  filterButton.addEventListener("click", () => {
    const weekOnly = filterButton.getAttribute("aria-pressed") !== "true";
    filterButton.setAttribute("aria-pressed", String(weekOnly));
    filterButton.setAttribute("aria-label", weekOnly ? "すべての記録を表示する" : "今週の記録だけ表示する");
    root.querySelectorAll<HTMLElement>("[data-record-group='month']").forEach((group) => { group.hidden = weekOnly; });
    showToast(weekOnly ? "今週の記録を表示しています" : "すべての記録を表示しています");
  });

  root.querySelector<HTMLElement>("[data-open-prayer-detail]")?.addEventListener("click", () => {
    renderPrayerDetailScreen();
  });

  requireElement<HTMLElement>("ion-tab-button[tab='chat']").addEventListener("click", (event) => {
    event.preventDefault();
    renderChatHome();
    focusAfterTransition(requireElement<HTMLElement>("#conversation"));
  });
  requireElement<HTMLElement>("ion-tab-button[tab='prayer']").addEventListener("click", (event) => {
    event.preventDefault();
    renderPrayerConfirmationScreen();
  });
  root.querySelectorAll<HTMLElement>("ion-tab-button[tab='profile']").forEach((tab) => {
    tab.addEventListener("click", (event) => {
      event.preventDefault();
      const label = tab.querySelector("ion-label")?.textContent ?? "この画面";
      showToast(`${label}は次の実装範囲です。`);
    });
  });
}

function bindPrayerDetailInteractions(): void {
  requireElement<HTMLButtonElement>("#prayer-detail-back").addEventListener("click", () => {
    renderRecordsScreen();
    focusAfterTransition(requireElement<HTMLElement>("[data-record-id='health']"));
  });
  root.querySelectorAll<HTMLButtonElement>("[data-view-prayer-pdf]").forEach((button) => {
    button.addEventListener("click", () => {
      prayerPdfReturnTarget = `[data-view-prayer-pdf='${button.dataset.viewPrayerPdf ?? "header"}']`;
      renderPrayerPdfScreen();
    });
  });
}

function bindPrayerPdfInteractions(): void {
  requireElement<HTMLButtonElement>("#prayer-pdf-back").addEventListener("click", () => {
    renderPrayerDetailScreen();
    focusAfterTransition(requireElement<HTMLElement>(prayerPdfReturnTarget));
  });
  root.querySelectorAll<HTMLButtonElement>("[data-pdf-action]").forEach((button) => {
    button.addEventListener("click", () => {
      showToast(button.dataset.pdfAction === "share"
        ? "共有機能は次の実装範囲です。"
        : "PDF保存は次の実装範囲です。");
    });
  });
}

function bindChatInteractions(): void {
  requireElement<HTMLButtonElement>("#menu-button").addEventListener("click", () => toggleSheet("menu-sheet"));
  requireElement<HTMLButtonElement>("#history-button").addEventListener("click", () => toggleSheet("history-sheet"));
  requireElement<HTMLElement>("#sheet-backdrop").addEventListener("click", () => toggleSheet());
  root.querySelectorAll<HTMLElement>("[data-close-sheet]").forEach((button) => {
    button.addEventListener("click", () => toggleSheet());
  });
  root.querySelectorAll<HTMLElement>(".app-sheet").forEach((sheet) => {
    sheet.addEventListener("keydown", handleSheetKeydown);
  });
  root.querySelector<HTMLButtonElement>("[data-about-aiqs]")?.addEventListener("click", () => {
    onboardingStep = 0;
    renderOnboardingScreen();
    focusOnboardingHeading();
  });
  root.querySelectorAll<HTMLElement>("[data-sheet-action]").forEach((button) => {
    button.addEventListener("click", () => {
      showToast(button.dataset.sheetAction ?? "");
      toggleSheet();
    });
  });

  root.querySelectorAll<HTMLButtonElement>("[data-reply]").forEach((button) => {
    button.addEventListener("click", () => {
      const reply = button.dataset.reply;
      root.querySelectorAll<HTMLButtonElement>("[data-reply]").forEach((candidate) => { candidate.disabled = true; });
      if (reply === "hearing") {
        appendMessage("user", "AIに詳細なヒアリングをしてもらう。");
        appendMessage("assistant", "あなたはなぜその内容を祈ってほしいですか？背景を教えてください。");
        return;
      }
      appendMessage("user", "AIから候補の提案を受ける。");
      appendMessage("assistant", "承知しました。祈りの言葉を整えました。");
      appendRitualInvite();
    });
  });

  requireElement<HTMLFormElement>("#composer-form").addEventListener("submit", (event) => {
    event.preventDefault();
    const input = requireElement<HTMLInputElement>("#message-input");
    const text = input.value.trim();
    if (!text) return;
    appendMessage("user", text);
    input.value = "";
    appendMessage("assistant", "受け取りました。もう少し詳しく聞かせてください。");
  });

  requireElement<HTMLElement>("ion-tab-button[tab='records']").addEventListener("click", (event) => {
    event.preventDefault();
    renderRecordsScreen();
  });
  requireElement<HTMLElement>("ion-tab-button[tab='prayer']").addEventListener("click", (event) => {
    event.preventDefault();
    renderPrayerConfirmationScreen();
  });
  root.querySelectorAll<HTMLElement>("ion-tab-button[tab='profile']").forEach((tab) => {
    tab.addEventListener("click", (event) => {
      event.preventDefault();
      const label = tab.querySelector("ion-label")?.textContent ?? "この画面";
      showToast(`${label}は次の実装範囲です。`);
    });
  });
}

function renderSessionGate(state: SessionGateState): void {
  stopRitualClock();
  if (isChatPreview || state.kind === "authenticated") {
    if (isPrayerPdfPreview) renderPrayerPdfScreen();
    else if (isPrayerDetailPreview) renderPrayerDetailScreen();
    else if (isRecordsPreview) renderRecordsScreen();
    else if (isPrayerConfirmationPreview) renderPrayerConfirmationScreen();
    else if (isOnboardingPreview) renderOnboardingScreen();
    else renderChatHome();
    return;
  }

  const copy = state.kind === "checking"
    ? ["確認中", "祈りの入口を、確かめています。", "端末に封じた認証状態を確認しています。"]
    : state.kind === "signedOut"
      ? ["未ログイン", "祈りの入口は、静かに整っています。", "ログインの準備が整うまで、外部通信は行いません。"]
      : state.kind === "error"
        ? ["安全停止", "いまは、ここで立ち止まります。", "認証状態を安全に確認できなかったため、接続を停止しました。"]
        : ["プレビュー", "ネイティブの器を、育てています。", "外部通信なしで、端末アプリの境界を確認しています。"];

  root.innerHTML = `
    <ion-app>
      <main class="gate-screen">
        <ion-icon name="sparkles-outline" aria-hidden="true"></ion-icon>
        <p>${copy[0]}</p>
        <h1>${copy[1]}</h1>
        <span>${copy[2]}</span>
      </main>
    </ion-app>
  `;
}

if (isPrayerPdfPreview) renderPrayerPdfScreen();
else if (isPrayerDetailPreview) renderPrayerDetailScreen();
else if (isRecordsPreview) renderRecordsScreen();
else if (isPrayerConfirmationPreview) renderPrayerConfirmationScreen();
else if (isOnboardingPreview) renderOnboardingScreen();
else if (isChatPreview) renderChatHome();

const sessionRuntime = createSessionGateRuntimeFromContract(authContract, {
  isNative,
  session: secureSession,
  onState: renderSessionGate,
});
void sessionRuntime.start();

if (isNative) {
  void startAppLifecycle({
    onDeepLink: () => undefined,
    onResume: () => { void sessionRuntime.resume(); },
  }).catch(() => undefined);
}
